import { useCallback, useEffect, useId, useState, type FormEvent, type ReactNode } from 'react';
import { ApiError, api } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import { useI18n } from '../i18n/I18nProvider.js';
import { Alert, Button, formFlag, formSecret, formText, TextField } from '../ui/index.js';

type SettingsView = Awaited<ReturnType<typeof api.get<'/api/v1/admin/settings'>>>;
type AdminsView = Awaited<ReturnType<typeof api.get<'/api/v1/admin/admins'>>>;
type ProvidersView = Awaited<ReturnType<typeof api.get<'/api/v1/admin/providers'>>>;
type AuditView = Awaited<ReturnType<typeof api.get<'/api/v1/admin/audit'>>>;
type SmtpTest = Awaited<ReturnType<typeof api.post<'/api/v1/admin/settings/smtp/test'>>>;

/**
 * Operating the deployment.
 *
 * Three things on this screen are secrets — the SMTP password, an AI provider's API key, and the
 * ability to make somebody else an administrator — and the screen is shaped around all three.
 *
 * **A secret is written, never read back.** The password field is empty on every load, including
 * right after a successful save. What comes back from the server is a boolean saying one is
 * stored. A masked value would be a real value passing through a browser, a crash report and a
 * support screenshot on its way to being useless anyway (INVARIANT I2).
 *
 * **Testing is offered next to saving.** Somebody who has just typed an SMTP password finds out
 * here whether it works, rather than a week later when a colleague cannot reset theirs.
 *
 * **Granting is an owner's action and the screen says so.** An administrator can change every
 * setting on this page and cannot create a second way in. The controls are hidden when the caller
 * is not an owner, and the server refuses regardless — the hiding is courtesy, not the control.
 */

type Tab = 'settings' | 'accounts' | 'providers' | 'admins' | 'audit';

const TABS: readonly Tab[] = ['settings', 'accounts', 'providers', 'admins', 'audit'];

type ProviderBody = Parameters<typeof api.post<'/api/v1/admin/providers'>>[1];
type ProviderKind = NonNullable<ProviderBody>['kind'];
type GrantBody = Parameters<typeof api.post<'/api/v1/admin/admins'>>[1];
type PlatformRole = NonNullable<GrantBody>['role'];

const PROVIDER_KINDS: readonly ProviderKind[] = [
  'ollama',
  'vllm',
  'openai',
  'openai_compatible',
  'anthropic',
  'gemini',
  'azure_openai',
];

const GRANTABLE_ROLES: readonly PlatformRole[] = ['operator', 'admin', 'owner'];

/* The form's own value is checked against the contract rather than asserted into it: a select
   whose options drift from the enum should fail here, not at the server with a 400 nobody can
   read. */
function asKind(value: string): ProviderKind {
  return PROVIDER_KINDS.find((kind) => kind === value) ?? 'ollama';
}

function asRole(value: string): PlatformRole {
  return GRANTABLE_ROLES.find((role) => role === value) ?? 'operator';
}

function codeOf(error: unknown): string {
  return error instanceof ApiError ? error.code : 'UNREACHABLE';
}

export function AdminScreen(): ReactNode {
  const { t, locale } = useI18n();
  const { platformRole } = useAuth();

  const [tab, setTab] = useState<Tab>('settings');
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [settings, setSettings] = useState<SettingsView | null>(null);
  const [admins, setAdmins] = useState<AdminsView | null>(null);
  const [providers, setProviders] = useState<ProvidersView | null>(null);
  const [audit, setAudit] = useState<AuditView | null>(null);
  const [smtpTest, setSmtpTest] = useState<SmtpTest | null>(null);
  const [saved, setSaved] = useState(false);

  const securityId = useId();

  /*
   * The address the providers have to be told to send people back to.
   *
   * Derived here from the origin the page is served from rather than sent by the server, because it
   * is the same derivation the server makes from `PUBLIC_APP_URL` — and if the two disagree, the
   * operator is looking at the one that is wrong.
   */
  const redirectUri = `${window.location.origin}/api/v1/mailboxes/oauth/callback`;
  const localityId = useId();
  const kindId = useId();
  const roleId = useId();

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const [nextSettings, nextAdmins, nextProviders, nextAudit] = await Promise.all([
        api.get('/api/v1/admin/settings', undefined, { signal }),
        api.get('/api/v1/admin/admins', undefined, { signal }),
        api.get('/api/v1/admin/providers', undefined, { signal }),
        api.get('/api/v1/admin/audit', { limit: 100 }, { signal }),
      ]);
      setSettings(nextSettings);
      setAdmins(nextAdmins);
      setProviders(nextProviders);
      setAudit(nextAudit);
      setFailure(null);
    } catch (error) {
      if (signal?.aborted === true) return;
      setFailure(codeOf(error));
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  /* Every mutation below reloads rather than patching local state: the server decides what a
     setting became, and two of these (a cleared secret, a revoked role) change rows this screen
     did not submit. */
  const run = useCallback(
    async (action: () => Promise<void>) => {
      setBusy(true);
      setSaved(false);
      try {
        await action();
        await load();
        setSaved(true);
        setFailure(null);
      } catch (error) {
        setFailure(codeOf(error));
      } finally {
        setBusy(false);
      }
    },
    [load],
  );

  const onSaveSettings = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const text = (name: string): string => formText(data, name);

    const next: Record<string, unknown> = {
      'platform.name': text('platform.name'),
      'platform.support_email': text('platform.support_email'),
      'platform.open_registration': formFlag(data, 'platform.open_registration'),
      'smtp.enabled': formFlag(data, 'smtp.enabled'),
      'smtp.host': text('smtp.host'),
      'smtp.port': Number(text('smtp.port') || 587),
      'smtp.security': text('smtp.security'),
      'smtp.username': text('smtp.username'),
      'smtp.from_address': text('smtp.from_address'),
      'smtp.from_name': text('smtp.from_name'),
      'oauth.google.client_id': text('oauth.google.client_id'),
      'oauth.microsoft.client_id': text('oauth.microsoft.client_id'),
      'oauth.microsoft.tenant': text('oauth.microsoft.tenant') || 'common',
    };

    /* An empty password field means "leave the stored one alone", not "clear it". Clearing is a
       separate, named button, because a blank field is what a browser hands back by default and
       destroying a working configuration should never be the default. */
    const password = formSecret(data, 'smtp.password');
    if (password !== '') next['smtp.password'] = password;

    /* Same rule as the SMTP password: an empty field means "leave the stored one alone". A client
       secret is not something anybody retypes to confirm they still want it. */
    for (const key of ['oauth.google.client_secret', 'oauth.microsoft.client_secret'] as const) {
      const secret = formSecret(data, key);
      if (secret !== '') next[key] = secret;
    }

    const form = event.currentTarget;
    void run(async () => {
      await api.patch('/api/v1/admin/settings', { settings: next });
      /* Reset rather than clear the one field: it is the only way to be sure the password does
         not survive somewhere in the DOM after a save. */
      form.reset();
    });
  };

  const onTestSmtp = () =>
    void run(async () => {
      setSmtpTest(await api.post('/api/v1/admin/settings/smtp/test'));
    });

  const onClearSmtpPassword = () =>
    void run(async () => {
      await api.patch('/api/v1/admin/settings', { settings: { 'smtp.password': null } });
    });

  const onRegisterProvider = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const text = (name: string): string => formText(data, name);
    const apiKey = formSecret(data, 'apiKey');
    const form = event.currentTarget;

    void run(async () => {
      await api.post('/api/v1/admin/providers', {
        slug: text('slug'),
        kind: asKind(text('kind')),
        locality: text('locality') === 'cloud' ? 'cloud' : 'local',
        baseUrl: text('baseUrl'),
        capabilities: text('capabilities')
          .split(',')
          .map((entry) => entry.trim())
          .filter((entry) => entry !== ''),
        priority: Number(text('priority') || 100),
        ...(apiKey === '' ? {} : { apiKey }),
      });
      form.reset();
    });
  };

  const onToggleProvider = (id: string, enabled: boolean) =>
    void run(async () => {
      await api.post(
        '/api/v1/admin/providers/{providerId}/enabled',
        { enabled },
        { providerId: id },
      );
    });

  const onGrant = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const form = event.currentTarget;
    void run(async () => {
      await api.post('/api/v1/admin/admins', {
        email: formText(data, 'email'),
        role: asRole(formText(data, 'role')),
      });
      form.reset();
    });
  };

  const onProvisionAccount = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const displayName = formText(data, 'displayName');
    const form = event.currentTarget;

    void run(async () => {
      await api.post('/api/v1/admin/accounts', {
        email: formText(data, 'email'),
        ...(displayName === '' ? {} : { displayName }),
        workspaceName: formText(data, 'workspaceName'),
        workspaceSlug: formText(data, 'workspaceSlug'),
        locale,
      });
      form.reset();
    });
  };

  const onRevoke = (userId: string) =>
    void run(async () => {
      await api.delete('/api/v1/admin/admins/{userId}', { userId });
    });

  if (settings === null || admins === null || providers === null || audit === null) {
    return failure === null ? (
      <div className="boot" role="status" aria-live="polite">
        {t('common.loading')}
      </div>
    ) : (
      <Alert tone="critical" title={t('admin.error.title')}>
        <p>{t('admin.error.body')}</p>
        <p className="today-list__meta">{failure}</p>
        <Button onClick={() => void load()}>{t('common.retry')}</Button>
      </Alert>
    );
  }

  /* Settings come back as `unknown`, because the contract says a value is whatever that key holds.
     Only a primitive is rendered into a field: an object here would become `[object Object]` and
     be saved back as the setting's new value on the next submit. */
  const value = (key: string): string => {
    const found: unknown = settings.settings[key as keyof typeof settings.settings];
    if (typeof found === 'string') return found;
    if (typeof found === 'number' || typeof found === 'bigint') return found.toString();
    return '';
  };
  const flag = (key: string): boolean =>
    settings.settings[key as keyof typeof settings.settings] === true;

  const mayGrant = admins.callerMayGrant;

  return (
    <>
      <header className="greeting">
        <h1 className="greeting__title">{t('admin.title')}</h1>
        <p className="empty__body">{t('admin.intro')}</p>
        {platformRole !== null && (
          <p className="today-list__meta">{t(`admin.youAre.${platformRole}`)}</p>
        )}
      </header>

      <div className="tabs" role="tablist" aria-label={t('admin.title')}>
        {TABS.map((entry) => (
          <button
            key={entry}
            type="button"
            role="tab"
            className={`tabs__tab${tab === entry ? ' tabs__tab--active' : ''}`}
            aria-selected={tab === entry}
            onClick={() => {
              setTab(entry);
              setSaved(false);
            }}
          >
            {t(`admin.tab.${entry}`)}
          </button>
        ))}
      </div>

      {failure !== null && (
        <Alert tone="critical" title={t('admin.error.title')}>
          <p>{t('admin.error.body')}</p>
          <p className="today-list__meta">{failure}</p>
        </Alert>
      )}

      {saved && failure === null && (
        <Alert tone="success" title={t('admin.saved')}>
          {t('admin.saved.body')}
        </Alert>
      )}

      {tab === 'settings' && (
        <section className="card">
          <h2 className="card__title">{t('admin.settings.title')}</h2>

          {/* Said before somebody discovers it by inviting a colleague who never hears from them. */}
          <Alert tone={settings.mail.configured && settings.mail.enabled ? 'success' : 'attention'}>
            {t(
              settings.mail.configured
                ? settings.mail.enabled
                  ? 'admin.mail.ready'
                  : 'admin.mail.disabled'
                : 'admin.mail.unconfigured',
            )}
          </Alert>

          <form className="mailbox-form" onSubmit={onSaveSettings}>
            <TextField
              name="platform.name"
              label={t('admin.field.platformName')}
              defaultValue={value('platform.name')}
            />
            <TextField
              name="platform.support_email"
              type="email"
              label={t('admin.field.supportEmail')}
              defaultValue={value('platform.support_email')}
            />
            <label className="field">
              <input
                type="checkbox"
                name="platform.open_registration"
                defaultChecked={flag('platform.open_registration')}
              />
              {t('admin.field.openRegistration')}
            </label>

            <h3 className="card__title">{t('admin.smtp.title')}</h3>
            <p className="empty__body">{t('admin.smtp.intro')}</p>

            <label className="field">
              <input type="checkbox" name="smtp.enabled" defaultChecked={flag('smtp.enabled')} />
              {t('admin.field.smtpEnabled')}
            </label>
            <TextField
              name="smtp.host"
              label={t('admin.field.smtpHost')}
              defaultValue={value('smtp.host')}
            />
            <TextField
              name="smtp.port"
              type="number"
              label={t('admin.field.smtpPort')}
              defaultValue={value('smtp.port') === '' ? '587' : value('smtp.port')}
            />
            <div className="field-group">
              <label className="field-group__label" htmlFor={securityId}>
                {t('admin.field.smtpSecurity')}
              </label>
              <select
                className="select"
                id={securityId}
                name="smtp.security"
                defaultValue={value('smtp.security') === '' ? 'starttls' : value('smtp.security')}
              >
                <option value="starttls">STARTTLS</option>
                <option value="tls">TLS</option>
                <option value="none">{t('admin.smtp.none')}</option>
              </select>
            </div>
            <TextField
              name="smtp.username"
              label={t('admin.field.smtpUsername')}
              defaultValue={value('smtp.username')}
            />
            {/*
              Never populated, in any state. The hint says whether one is stored, which is the only
              thing about a stored secret that may leave the server.
            */}
            <TextField
              name="smtp.password"
              type="password"
              autoComplete="new-password"
              label={t('admin.field.smtpPassword')}
              hint={t(
                settings.secretsPresent['smtp.password'] === true
                  ? 'admin.secret.stored'
                  : 'admin.secret.absent',
              )}
            />
            <TextField
              name="smtp.from_address"
              type="email"
              label={t('admin.field.fromAddress')}
              defaultValue={value('smtp.from_address')}
            />
            <TextField
              name="smtp.from_name"
              label={t('admin.field.fromName')}
              defaultValue={value('smtp.from_name')}
            />

            <h3 className="card__title">{t('admin.oauth.title')}</h3>
            <p className="empty__body">{t('admin.oauth.intro')}</p>
            <Alert tone="info">{t('admin.oauth.redirect', { url: redirectUri })}</Alert>

            <TextField
              name="oauth.google.client_id"
              label={t('admin.field.googleClientId')}
              defaultValue={value('oauth.google.client_id')}
            />
            <TextField
              name="oauth.google.client_secret"
              type="password"
              autoComplete="new-password"
              label={t('admin.field.googleClientSecret')}
              hint={t(
                settings.secretsPresent['oauth.google.client_secret'] === true
                  ? 'admin.secret.stored'
                  : 'admin.secret.absent',
              )}
            />
            <TextField
              name="oauth.microsoft.client_id"
              label={t('admin.field.microsoftClientId')}
              defaultValue={value('oauth.microsoft.client_id')}
            />
            <TextField
              name="oauth.microsoft.client_secret"
              type="password"
              autoComplete="new-password"
              label={t('admin.field.microsoftClientSecret')}
              hint={t(
                settings.secretsPresent['oauth.microsoft.client_secret'] === true
                  ? 'admin.secret.stored'
                  : 'admin.secret.absent',
              )}
            />
            <TextField
              name="oauth.microsoft.tenant"
              label={t('admin.field.microsoftTenant')}
              hint={t('admin.field.microsoftTenantHint')}
              defaultValue={
                value('oauth.microsoft.tenant') === '' ? 'common' : value('oauth.microsoft.tenant')
              }
            />

            {smtpTest !== null && (
              <Alert
                tone={smtpTest.ok ? 'success' : 'critical'}
                title={t(smtpTest.ok ? 'admin.smtp.ok' : 'admin.smtp.failed')}
              >
                {/* The server's own words, which are the only useful thing when a handshake fails. */}
                {smtpTest.message ?? t('admin.smtp.noDetail')}
              </Alert>
            )}

            <div className="mailbox-form__actions">
              <Button type="submit" variant="primary" disabled={busy} loading={busy}>
                {t('common.save')}
              </Button>
              <Button type="button" disabled={busy} onClick={onTestSmtp}>
                {t('admin.smtp.test')}
              </Button>
              {settings.secretsPresent['smtp.password'] === true && (
                <Button
                  type="button"
                  variant="danger"
                  disabled={busy}
                  onClick={onClearSmtpPassword}
                >
                  {t('admin.smtp.clearPassword')}
                </Button>
              )}
            </div>
          </form>
        </section>
      )}

      {tab === 'accounts' && (
        <section className="card">
          <h2 className="card__title">{t('admin.accounts.title')}</h2>
          <p className="empty__body">{t('admin.accounts.intro')}</p>

          {!settings.mail.configured || !settings.mail.enabled ? (
            <Alert tone="attention" title={t('admin.accounts.mailRequired.title')}>
              {t('admin.accounts.mailRequired.body')}
            </Alert>
          ) : null}

          {platformRole === 'operator' ? (
            <Alert tone="info">{t('admin.accounts.adminOnly')}</Alert>
          ) : (
            <form className="mailbox-form" onSubmit={onProvisionAccount}>
              <TextField name="email" type="email" label={t('admin.accounts.email')} required />
              <TextField name="displayName" label={t('admin.accounts.displayName')} />
              <TextField name="workspaceName" label={t('admin.accounts.workspaceName')} required />
              <TextField
                name="workspaceSlug"
                label={t('admin.accounts.workspaceSlug')}
                hint={t('admin.accounts.workspaceSlugHint')}
                pattern="[a-z0-9][a-z0-9\\x2d]*[a-z0-9]"
                minLength={3}
                maxLength={64}
                required
              />
              <div className="mailbox-form__actions">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={busy || !settings.mail.configured || !settings.mail.enabled}
                  loading={busy}
                >
                  {t('admin.accounts.create')}
                </Button>
              </div>
            </form>
          )}
        </section>
      )}

      {tab === 'providers' && (
        <section className="card">
          <h2 className="card__title">{t('admin.providers.title')}</h2>
          <p className="empty__body">{t('admin.providers.intro')}</p>

          {providers.providers.length === 0 ? (
            <p className="empty__body">{t('admin.providers.empty')}</p>
          ) : (
            <ul className="today-list">
              {providers.providers.map((provider) => (
                <li className="today-list__item" key={provider.id}>
                  <span
                    className={`badge badge--${provider.enabled && provider.healthy ? 'ok' : 'critical'}`}
                  >
                    {t(
                      provider.enabled
                        ? provider.healthy
                          ? 'admin.provider.healthy'
                          : 'admin.provider.unhealthy'
                        : 'admin.provider.off',
                    )}
                  </span>
                  <span className="today-list__subject">{provider.slug}</span>
                  {/* Locality is shown on every row: it is what a local-only workspace policy is
                      enforced against, so it is not a detail behind a click. */}
                  <span className="today-list__meta">
                    {t(`admin.locality.${provider.locality}`)} · {provider.kind} ·{' '}
                    {provider.baseUrl}
                  </span>
                  {provider.lastError !== null && (
                    <span className="today-list__meta">{provider.lastError}</span>
                  )}
                  <Button
                    variant="ghost"
                    disabled={busy}
                    onClick={() => onToggleProvider(provider.id, !provider.enabled)}
                  >
                    {t(provider.enabled ? 'admin.provider.disable' : 'admin.provider.enable')}
                  </Button>
                </li>
              ))}
            </ul>
          )}

          <h3 className="card__title">{t('admin.provider.add')}</h3>
          <form className="mailbox-form" onSubmit={onRegisterProvider}>
            <TextField name="slug" label={t('admin.field.slug')} required />
            <div className="field-group">
              <label className="field-group__label" htmlFor={kindId}>
                {t('admin.field.kind')}
              </label>
              <select className="select" id={kindId} name="kind" defaultValue="ollama">
                {PROVIDER_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {t(`admin.kind.${kind}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="field-group">
              <label className="field-group__label" htmlFor={localityId}>
                {t('admin.field.locality')}
              </label>
              <select className="select" id={localityId} name="locality" defaultValue="local">
                <option value="local">{t('admin.locality.local')}</option>
                <option value="cloud">{t('admin.locality.cloud')}</option>
              </select>
            </div>
            <TextField name="baseUrl" type="url" label={t('admin.field.baseUrl')} required />
            <TextField
              name="apiKey"
              type="password"
              autoComplete="new-password"
              label={t('admin.field.apiKey')}
              hint={t('admin.field.apiKeyHint')}
            />
            <TextField
              name="capabilities"
              label={t('admin.field.capabilities')}
              hint={t('admin.field.capabilitiesHint')}
            />
            <TextField
              name="priority"
              type="number"
              label={t('admin.field.priority')}
              defaultValue="100"
            />
            <div className="mailbox-form__actions">
              <Button type="submit" variant="primary" disabled={busy} loading={busy}>
                {t('admin.provider.register')}
              </Button>
            </div>
          </form>
        </section>
      )}

      {tab === 'admins' && (
        <section className="card">
          <h2 className="card__title">{t('admin.admins.title')}</h2>
          <p className="empty__body">{t('admin.admins.intro')}</p>

          <ul className="today-list">
            {admins.admins.map((entry) => (
              <li className="today-list__item" key={entry.userId}>
                <span className="badge badge--info">{t(`admin.role.${entry.role}`)}</span>
                <span className="today-list__subject">{entry.email}</span>
                {mayGrant && (
                  <Button variant="ghost" disabled={busy} onClick={() => onRevoke(entry.userId)}>
                    {t('admin.admins.revoke')}
                  </Button>
                )}
              </li>
            ))}
          </ul>

          {mayGrant ? (
            <form className="mailbox-form" onSubmit={onGrant}>
              <TextField name="email" type="email" label={t('admin.field.grantEmail')} required />
              <div className="field-group">
                <label className="field-group__label" htmlFor={roleId}>
                  {t('admin.field.role')}
                </label>
                <select className="select" id={roleId} name="role" defaultValue="operator">
                  {GRANTABLE_ROLES.map((role) => (
                    <option key={role} value={role}>
                      {t(`admin.role.${role}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mailbox-form__actions">
                <Button type="submit" variant="primary" disabled={busy} loading={busy}>
                  {t('admin.admins.grant')}
                </Button>
              </div>
            </form>
          ) : (
            /* Said rather than silently omitted: an administrator who cannot find the grant button
               should know it is a rule, not a bug. */
            <Alert tone="info">{t('admin.admins.ownerOnly')}</Alert>
          )}
        </section>
      )}

      {tab === 'audit' && (
        <section className="card">
          <h2 className="card__title">{t('admin.audit.title')}</h2>
          <p className="empty__body">{t('admin.audit.intro')}</p>
          <ul className="today-list">
            {audit.events.map((event) => (
              <li className="today-list__item" key={event.id}>
                <span className="today-list__subject">{event.action}</span>
                <span className="today-list__meta">
                  {new Date(event.occurredAt).toLocaleString(locale)} · {event.resourceType}
                  {event.resourceId === null ? '' : ` · ${event.resourceId}`}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
